/**
 * Voice AI — Media Service
 * API calls for general-purpose media library CRUD.
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';

class MediaService {
  /**
   * Load media files with optional filters.
   * @param {Object} params
   * @param {string} [params.fileType] - Filter by file_type (audio/image/video/document)
   * @param {string} [params.search] - Search by original_name
   * @param {string} [params.tags] - Filter by tags (comma-separated, any match)
   * @returns {Promise<{success: boolean, files: Array, total: number}>}
   */
  async loadFiles(params = {}) {
    const query = new URLSearchParams();
    if (params.fileType) query.set('file_type', params.fileType);
    if (params.search) query.set('search', params.search);
    if (params.tags) query.set('tags', params.tags);
    const qs = query.toString();
    const res = await authService.apiFetch(`${API_BASE}/media.php${qs ? '?' + qs : ''}`);
    return res.json();
  }

  /**
   * Upload a single file.
   * @param {File} file - File object from input or drag-drop
   * @param {Object} [options]
   * @param {string} [options.tags] - Comma-separated tags
   * @param {string} [options.uploadedBy] - Source context (e.g. 'realism', 'ivr')
   * @returns {Promise<{success: boolean, media: Object, id: number}>}
   */
  async uploadFile(file, { tags, uploadedBy, displayName } = {}) {
    const form = new FormData();
    form.append('file', file);
    if (tags) form.append('tags', tags);
    if (uploadedBy) form.append('uploaded_by', uploadedBy);
    if (displayName) form.append('display_name', displayName);
    const res = await authService.apiFetch(`${API_BASE}/media.php`, {
      method: 'POST',
      body: form,
    });
    return res.json();
  }

  /**
   * Delete a media file by ID.
   * @param {number} id
   * @returns {Promise<{success: boolean}>}
   */
  async deleteFile(id) {
    const res = await authService.apiFetch(`${API_BASE}/media.php?id=${id}`, {
      method: 'DELETE',
    });
    return res.json();
  }
}

export const mediaService = new MediaService();
